<template>
  <div class="redeem-form">
    <img
      class="gift-image"
      src="../../../assets/images/gift/redeemcard.png"
      alt="gift card"
    />
    <h1 class="title">Redeem your gift</h1>
    <p class="description">Enter your code below to get started.</p>
    <form @submit.prevent="submit">
      <input
        type="text"
        v-model="codes"
        maxlength="6"
        ref="codesInput"
        @blur="updateFocusing(0)"
        @input="updateFocusing(1)"
      />
      <div class="code-container" @click="focus">
        <div
          class="code"
          v-for="(code, i) in codes.split('')"
          :key="i"
          :class="{ error: error }"
        >
          {{ code.toUpperCase() }}
        </div>
        <div
          class="code empty"
          :class="{ active: focusingIndex === i, error: error }"
          v-for="i in 6 - codes.split('').length"
          :key="`empty${i}`"
        >
          *
          <span class="focusing-bar">|</span>
        </div>
      </div>
      <div class="error-message" :class="{ showing: error }">{{ error }}</div>
      <button class="submit-btn">Redeem Now</button>
    </form>
    <div class="help-section">
      <h3 class="help-title">
        Need help?
        <span class="contact-link" @click="startHelpChat">Contact us.</span>
      </h3>
      <small
        >By redeeming, you agree to the ProGuides
        <a target="__blank" href="/terms_and_conditions"
          >Terms and Conditions.</a
        ></small
      >
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
export default {
  data: function () {
    return {
      codes: "",
      focusingIndex: 0,
      error: "",
      submitting: false,
    }
  },
  computed: {
    ...mapGetters("User", ["getUser"]),
  },
  methods: {
    ...mapActions("Store/Gift", ["redeemGift"]),
    ...mapActions("Util", ["startHelpChat"]),
    focus () {
      this.$refs.codesInput.focus()
      this.focusingIndex = 1
      this.error = ""
    },
    updateFocusing (index) {
      this.focusingIndex = index
    },
    async submit () {
      if (this.submitting) {
        return
      }
      this.trackAnalyticsEvent("Attempt gift code redemption", {
        code: this.codes,
      })
      this.submitting = true
      this.error = ""
      if (!this.getUser) {
        this.$emit("toggleLoginModal")
        this.submitting = false
        return
      }
      if (this.codes.length !== 6) {
        this.error = "Invalid code. Try again"
        this.submitting = false
        return
      }
      const data = await this.redeemGift(this.codes.toUpperCase())
      if (data.error) {
        this.error = data.error
        this.submitting = false
      } else {
        this.submitting = false
        const perks = data.perks_applied
        localStorage.setItem("giftPerks", JSON.stringify(perks))
        window.location.href = "/dashboard"
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
* {
  margin: 0;
}
.redeem-form {
  min-height: calc(100vh - 5.5rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @include tablet-down {
    padding: 8.7rem 0 13.2rem;
  }
  @include phone-small {
    padding: 5rem 0 10rem;
  }
  .gift-image {
    width: 44rem;
    @include tablet-down {
      width: 29.2rem;
    }
  }
  .title {
    font-family: Proxima Nova;
    font-style: normal;
    font-weight: 800;
    font-size: 5.4rem;
    line-height: 96.3%;
    color: #3b3b3b;
    margin-top: 0.8rem;
    @include tablet-down {
      font-size: 4rem;
    }
  }
  .description {
    margin-top: 1.6rem;
    font-family: Proxima Nova;
    font-style: normal;
    font-weight: normal;
    font-size: 2rem;
    line-height: 93%;
    color: #3b3b3b;
    @include tablet-down {
      margin-top: 1.1rem;
    }
  }
  form {
    margin-top: 3.4rem;
    display: flex;
    align-items: center;
    flex-direction: column;
    @include tablet-down {
      margin-top: 2.5rem;
    }
    input {
      opacity: 0;
      position: absolute;
    }
    .code-container {
      display: flex;
      .code {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 6.7rem;
        height: 8.5rem;
        box-shadow: 0px 4px 5px rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        font-family: Proxima Nova;
        font-style: normal;
        font-weight: normal;
        font-size: 4rem;
        line-height: 93%;
        outline: none;
        color: #3b3b3b;
        border: 1px solid #3b3b3b;
        text-align: center;
        margin-right: 1rem;
        cursor: text;
        &:last-child {
          margin-right: 0;
        }
        @include tablet-down {
          width: 4.916rem;
          height: 6.236rem;
          font-size: 2.93481rem;
        }
        &.empty {
          border-color: #e2e8ee;
          color: #e2e8ee;
          .focusing-bar {
            display: none;
          }
          &.active {
            border-color: #3b3b3b;
            .focusing-bar {
              display: block;
              color: #3b3b3b;
              position: absolute;
              left: 60%;
            }
          }
        }
        &.error {
          border-color: #db1d3f;
        }
      }
    }
    .error-message {
      margin-top: 2.7rem;
      padding: 0 4rem;
      min-height: 3.3rem;
      min-width: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(219, 29, 63, 0.05);
      font-family: Proxima Nova;
      font-style: normal;
      font-weight: normal;
      font-size: 1.4rem;
      line-height: 93%;
      color: #ff354d;
      border-radius: 4px;
      max-width: 100%;
      opacity: 0;
      @include tablet-down {
        margin-top: 2rem;
      }
      &.showing {
        opacity: 1;
      }
    }
    .submit-btn {
      display: block;
      margin-top: 2.3rem;
      width: 21.8rem;
      height: 5.1rem;
      background: #f24462;
      border-radius: 4px;
      font-family: Proxima Nova;
      font-style: normal;
      font-weight: bold;
      font-size: 1.6rem;
      line-height: 93%;
      color: #ffffff;
      outline: none;
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      @include tablet-down {
        margin-top: 1rem;
      }
      &:hover {
        background: #db1d3f;
      }
    }
  }
  .help-section {
    margin-top: 6.3rem;
    @include tablet-down {
      margin-top: 4.3rem;
    }
    @include phone-small {
      margin-top: 3rem;
    }
    .help-title {
      font-family: Proxima Nova;
      font-style: normal;
      font-weight: normal;
      font-size: 1.4rem;
      line-height: 93%;
      color: #3b3b3b;
      .contact-link {
        color: #ff354d;
        &:hover {
          opacity: 0.8;
          cursor: pointer;
        }
      }
    }
    small {
      display: block;
      margin-top: 1rem;
      font-family: Proxima Nova;
      font-style: normal;
      font-weight: normal;
      font-size: 1rem;
      line-height: 1.2rem;
      color: #3b3b3b;
      a {
        color: #3b3b3b;
        text-decoration: underline;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/gift/redeem/RedeemForm.vue